import { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Users, 
  Mail, 
  LogOut, 
  Menu,
  X,
  ChevronRight,
  RefreshCcw,
  UserPlus,
  Briefcase,
  Calculator,
  Lightbulb,
  BookOpen,
  Shield
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_cxo-placement/artifacts/o33cdnbv_profile_pic.png";

const sidebarLinks = [
  { href: '/admin', label: 'Dashboard', icon: LayoutDashboard },
  { href: '/admin/clients', label: 'Clients', icon: Users },
  { href: '/admin/contacts', label: 'Inquiries', icon: Mail },
  { href: '/admin/ccfs-leads', label: 'CCFS Leads', icon: RefreshCcw },
  { href: '/admin/cases', label: 'R-PACE Cases', icon: Briefcase },
  { href: '/admin/partner-requests', label: 'R-PACE Partners', icon: UserPlus },
  { href: '/admin/calculator', label: 'CCFS Calculator', icon: Calculator },
  { href: '/admin/form-library', label: 'Form Library', icon: BookOpen },
  { href: '/admin/scheme-settings', label: 'Scheme Settings', icon: Shield },
  { href: '/admin/tips', label: 'Partner Tips', icon: Lightbulb },
];

export const AdminLayout = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const admin = JSON.parse(localStorage.getItem('ratnatraya_admin') || '{}');

  const isActive = (href) => {
    if (href === '/admin') return location.pathname === '/admin';
    return location.pathname.startsWith(href);
  };

  const handleLogout = () => {
    localStorage.removeItem('ratnatraya_token');
    localStorage.removeItem('ratnatraya_admin');
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7]" data-testid="admin-layout">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-slate-200 h-16">
        <div className="flex items-center justify-between px-4 h-full">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg"
              data-testid="admin-mobile-menu-btn"
            >
              {isSidebarOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
            <img src={LOGO_URL} alt="Ratnatraya" className="h-8 w-8" />
            <span className="font-semibold text-slate-900">Admin</span>
          </div>
        </div>
      </header>

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-64 bg-white border-r border-slate-200 transform transition-transform duration-200 lg:translate-x-0 ${
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-testid="admin-sidebar"
      >
        {/* Sidebar Header */}
        <div className="h-16 flex items-center gap-3 px-6 border-b border-slate-100">
          <img src={LOGO_URL} alt="Ratnatrraya" className="h-9 w-9" />
          <div>
            <span className="font-secondary font-semibold text-slate-900 block">Ratnatrraya</span>
            <span className="text-xs text-slate-500">Client Vault</span>
          </div>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-1">
          {sidebarLinks.map((link) => {
            const Icon = link.icon;
            return (
              <Link
                key={link.href}
                to={link.href}
                onClick={() => setIsSidebarOpen(false)}
                data-testid={`admin-nav-${link.label.toLowerCase()}`}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                  isActive(link.href)
                    ? 'bg-red-50 text-[#E84C3D]'
                    : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                <Icon size={20} />
                {link.label}
                {isActive(link.href) && <ChevronRight size={16} className="ml-auto" />}
              </Link>
            );
          })}
        </nav>

        {/* Sidebar Footer */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-100">
          <div className="flex items-center gap-3 px-4 py-2 mb-2">
            <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 font-medium">
              {admin.name?.charAt(0) || 'A'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{admin.name || 'Admin'}</p>
              <p className="text-xs text-slate-500 truncate">{admin.email || ''}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            className="w-full justify-start text-slate-600 hover:text-red-600 hover:bg-red-50"
            onClick={handleLogout}
            data-testid="admin-logout-btn"
          >
            <LogOut size={18} className="mr-2" />
            Logout
          </Button>
          <Link to="/" className="block mt-2">
            <Button variant="outline" className="w-full text-sm">
              Back to Website
            </Button>
          </Link>
        </div>
      </aside>

      {/* Overlay */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/20 z-30 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen">
        <Outlet />
      </main>
    </div>
  );
};

export default AdminLayout;
